import { NpcEditor } from '@/components/admin/npc/npc-editor';
import {
  NpcOriginCard,
  NpcPrefillBanner,
  NpcPrefillRefusal,
} from '@/components/admin/npc/npc-origin';
import { useAdminSession } from '@/lib/admin-session';
import { ApiError, fetchNpcPrefill } from '@/lib/api';
import { prefillReasonOf } from '@/lib/npc-labels';
import { readNpcNewSearch } from '@/lib/npc-search';
import { Spinner } from '@pms-digital/design-system';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';

/**
 * Le NPC neuf (npc.md §4, issue #210): the empty fiche (§4.1) and, with `?depuis=<sessionId>`, the
 * fiche prefilled from a finished session (§4.2, H18) - the page « Créer un NPC » of lot F lands on.
 *
 * ## It calls almost nothing
 *
 * « Avant ce premier enregistrement il n'existe ni entité ni brouillon serveur » (§4 of the
 * refonte): the empty editor has no entity query, no autosave and nothing to reload, which is why
 * it is a route of its own and not `/admin/npc/$npcId` with a sentinel id. Written once, the first
 * save writes the entity and its v1 together and `onCreated` opens the fiche.
 *
 * ## The prefill is read once and refused in French
 *
 * `GET /api/npcs/prefill?session=` answers the draft or a 409 `prefill-refused` whose `reason` says
 * why (a replay, a session still running, no protocol page, no turn): the page words it and draws
 * the way back, and creates nothing. The draft is not a server draft - nothing exists until the v1
 * is saved - so it is read with `staleTime` and `gcTime` at zero and `refetchOnWindowFocus` off:
 * the editor seeds its state from it ONCE, and a fresh read behind it would be a draft the
 * operator did not see. The editor is keyed on the session so that another `?depuis=` is another
 * draft.
 *
 * The static segment wins over the dynamic one in TanStack Router, so `/admin/npc/nouveau` can never
 * be read as an NPC called « nouveau ».
 */

export const Route = createFileRoute('/admin/npc/nouveau')({
  validateSearch: readNpcNewSearch,
  component: NouveauNpcPage,
});

function NouveauNpcPage() {
  const { depuis } = Route.useSearch();
  return (
    <div className="space-y-6">
      <p className="text-gray-600 text-small">
        <Link to="/admin/npc" className="text-bleu-nuit underline underline-offset-2">
          NPC
        </Link>
        <span className="mx-2">›</span>
        Nouveau NPC
      </p>
      {depuis === undefined ? (
        <NpcEditor
          detail={null}
          viewing={null}
          onView={() => undefined}
          onCloseView={() => undefined}
          onReload={() => undefined}
        />
      ) : (
        <PrefilledNpc key={depuis} sessionId={depuis} />
      )}
    </div>
  );
}

function PrefilledNpc({ sessionId }: { sessionId: string }) {
  const { email } = useAdminSession();
  const prefill = useQuery({
    queryKey: ['npc-prefill', email, sessionId],
    queryFn: () => fetchNpcPrefill(sessionId),
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });

  if (prefill.isPending) {
    return (
      <div className="flex justify-center py-12">
        <Spinner label="Préparation du brouillon" />
      </div>
    );
  }
  if (prefill.isError) {
    return (
      <NpcPrefillRefusal
        reason={prefillReasonOf(prefill.error)}
        sessionId={sessionId}
        gone={prefill.error instanceof ApiError && prefill.error.status === 404}
      />
    );
  }
  return (
    <NpcEditor
      detail={null}
      viewing={null}
      prefill={prefill.data}
      banner={<NpcPrefillBanner sessionId={prefill.data.sessionId} />}
      asideTop={<NpcOriginCard sessionId={prefill.data.sessionId} />}
      onView={() => undefined}
      onCloseView={() => undefined}
      onReload={() => undefined}
    />
  );
}
